/**
 * PWWCapital — Frontend Performance Monitoring (Section 31)
 *
 * Tracks Web Vitals (CLS, FID, FCP, LCP, TTFB) and API call latency.
 * Reports are batched and sent to the backend analytics endpoint.
 * Falls back gracefully if the endpoint is unavailable.
 *
 * Usage:
 *   // In index.js:
 *   import { initPerformanceMonitoring } from './utils/performance';
 *   initPerformanceMonitoring();
 *
 *   // Track an API call:
 *   import { trackApiCall } from './utils/performance';
 *   const t = trackApiCall('/api/accounts/');
 *   try { await fetchAccounts(); t.success(); }
 *   catch { t.failure(); }
 */

//  Configuration

const REPORT_ENDPOINT = '/api/telemetry/vitals/';
const FLUSH_INTERVAL_MS = 30_000;
const MAX_QUEUE_SIZE = 50;

// Alert thresholds (milliseconds unless noted)
const THRESHOLDS = {
  LCP:  { good: 2500, poor: 4000 },
  FID:  { good:  100, poor:  300 },
  CLS:  { good: 0.1,  poor: 0.25 },  // unitless score
  FCP:  { good: 1800, poor: 3000 },
  TTFB: { good:  800, poor: 1800 },
  API_CALL: { good: 500, poor: 2000 },
};

//  Queue

let queue = [];
let flushTimer = null;

function enqueue(entry) {
  queue.push({ ...entry, timestamp: new Date().toISOString() });
  if (queue.length >= MAX_QUEUE_SIZE) {
    flushMetrics();
  } else {
    scheduleFlush();
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flushMetrics, FLUSH_INTERVAL_MS);
}

async function flushMetrics() {
  clearTimeout(flushTimer);
  flushTimer = null;
  if (!queue.length) return;

  const batch = queue.splice(0, queue.length);
  try {
    // Use sendBeacon for reliability during page unload
    if (navigator.sendBeacon) {
      navigator.sendBeacon(REPORT_ENDPOINT, JSON.stringify({ metrics: batch }));
    } else {
      fetch(REPORT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metrics: batch }),
        keepalive: true,
      }).catch(() => {}); // silent — telemetry never disrupts the user
    }
  } catch {
    // Silently discard — monitoring failures must never surface to the user
  }
}

//  Rating helpers

function getRating(metricName, value) {
  const t = THRESHOLDS[metricName];
  if (!t) return 'unknown';
  if (value <= t.good) return 'good';
  if (value <= t.poor) return 'needs-improvement';
  return 'poor';
}

//  Web Vitals

/**
 * Initialise Web Vitals collection and reporting.
 * Dynamically imports `web-vitals` (already in CRA devDependencies).
 */
export async function initPerformanceMonitoring() {
  if (process.env.NODE_ENV === 'test') return;

  try {
    const { onCLS, onFID, onFCP, onLCP, onTTFB } = await import('web-vitals');

    const handle = ({ name, value, id, navigationType }) => {
      const rating = getRating(name, value);
      enqueue({ type: 'vital', name, value, rating, id, navigationType });

      // Log poor metrics to console in development for immediate feedback
      if (process.env.NODE_ENV === 'development' && rating === 'poor') {
        console.warn(`[PWW Perf] Poor ${name}: ${value.toFixed(2)} (threshold: ${THRESHOLDS[name]?.poor})`);
      }
    };

    onCLS(handle);
    onFID(handle);
    onFCP(handle);
    onLCP(handle);
    onTTFB(handle);
  } catch {
    // web-vitals not available — graceful no-op
  }

  // Flush on page hide / unload
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushMetrics();
  });
  window.addEventListener('beforeunload', flushMetrics);
}

//  API Call Tracking

/**
 * Measure the latency of an API call.
 * Returns an object with `success()` and `failure()` to finalise the measurement.
 *
 * @param {string} endpoint  URL path (not full URL, never include PII)
 * @param {string} [method='GET']
 * @returns {{ success(statusCode?: number): void; failure(statusCode?: number): void }}
 *
 * @example
 *   const t = trackApiCall('/accounts/', 'GET');
 *   try { await apiService.getAccounts(); t.success(200); }
 *   catch (e) { t.failure(e.response?.status); }
 */
export function trackApiCall(endpoint, method = 'GET') {
  const start = performance.now();

  const finish = (success, statusCode) => {
    const duration = Math.round(performance.now() - start);
    const rating = getRating('API_CALL', duration);

    enqueue({
      type: 'api',
      endpoint: sanitizeEndpoint(endpoint),
      method: method.toUpperCase(),
      duration,
      rating,
      success,
      statusCode,
    });

    if (process.env.NODE_ENV === 'development' && rating === 'poor') {
      console.warn(`[PWW Perf] Slow API ${method} ${endpoint}: ${duration}ms`);
    }
  };

  return {
    success: (statusCode = 200) => finish(true,  statusCode),
    failure: (statusCode = 0)   => finish(false, statusCode),
  };
}

/**
 * Strip dynamic URL segments (IDs) to prevent high-cardinality keys.
 * e.g. '/accounts/123/transactions/' → '/accounts/:id/transactions/'
 */
function sanitizeEndpoint(url) {
  return url
    .replace(/\/\d+\//g, '/:id/')
    .replace(/\/[0-9a-f-]{36}\//gi, '/:uuid/');
}

//  React render performance

/**
 * Log slow React component renders (>16 ms = missed 60 fps frame).
 * Intended for use with React Profiler in development.
 *
 * @param {string} id
 * @param {'mount'|'update'} phase
 * @param {number} actualDuration
 */
export function onRenderCallback(id, phase, actualDuration) {
  if (process.env.NODE_ENV !== 'development') return;
  if (actualDuration > 16) {
    console.warn(`[PWW Render] Slow component "${id}" (${phase}): ${actualDuration.toFixed(1)}ms`);
  }
}
